import React, { ReactElement, useContext } from 'react'

import { LanguageContext } from '../../Common/Contexts'
import LanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { StatisticsConfiguration } from './Types'
import { SortList as SortListLanguage } from '../../../../i18n'
import Checkbox from '../../Common/Components/Checkbox'
import Ul, { Li } from '../../Common/Components/Ul'
import {
    STATISTICS_ORDERING_ISDESCENDING,
    STATISTICS_ORDERING_NONE,
    STATISTICS_ORDERING_PRIORITY_1ST,
    STATISTICS_ORDERING_PRIORITY_2ND,
    STATISTICS_ORDERING_PRIORITY_3RD,
    STATISTICS_ORDERING_PRIORITY_MASK
} from './Constants'

type SortListProps = {
    selectedStatisticsConfiguration: StatisticsConfiguration
    configuredStatisticsConfigurations: StatisticsConfiguration[]
    setConfiguredStatisticsConfigurations: (value: StatisticsConfiguration[]) => void
    handleSortClose: () => void
}

/**
 * Statistics field Sorting options list component
 *
 * @param selectedStatisticsConfiguration Currently selected statistics configuration object.
 * @param configuredStatisticsConfigurations Array of all currently configured statistics.
 * @param setConfiguredStatisticsConfigurations Setter for related property.
 * @param handleSortClose Callback to close sort dialog / modal where list content is rendered.
 */
const SortListContent: React.FC<SortListProps> = ({
    selectedStatisticsConfiguration,
    configuredStatisticsConfigurations,
    setConfiguredStatisticsConfigurations,
    handleSortClose
}): ReactElement => {
    const language = useContext(LanguageContext) as LanguageHandler
    language.loadBundle('SortList')

    const selectedStatisticsFieldOrderingValue = selectedStatisticsConfiguration?.ordering || 0
    const selectedStatisticsFieldOrderingPriority =
        selectedStatisticsFieldOrderingValue & STATISTICS_ORDERING_PRIORITY_MASK
    const selectedStatisticsFieldOrderingValueIsDescending =
        (selectedStatisticsFieldOrderingValue & STATISTICS_ORDERING_ISDESCENDING) === STATISTICS_ORDERING_ISDESCENDING
    const notSorted = selectedStatisticsFieldOrderingValue === STATISTICS_ORDERING_NONE

    const configuredStatisticsWithSortingCount = configuredStatisticsConfigurations.filter(
        (statisticConfiguration) =>
            (statisticConfiguration.ordering & STATISTICS_ORDERING_PRIORITY_MASK) !== STATISTICS_ORDERING_NONE
    ).length

    const shouldRender3rdSortingOption =
        configuredStatisticsWithSortingCount === 3 ||
        (configuredStatisticsWithSortingCount === 2 &&
            selectedStatisticsFieldOrderingPriority !== STATISTICS_ORDERING_PRIORITY_2ND &&
            selectedStatisticsFieldOrderingPriority !== STATISTICS_ORDERING_PRIORITY_1ST)

    const setCurrentlySelectedStatisticsConfigurationSortingToNone = (): void => {
        // Only do something if sorting was not already set to none.
        if (selectedStatisticsConfiguration.ordering !== STATISTICS_ORDERING_NONE) {
            // Set sorting for current field
            setConfiguredStatisticsConfigurations([
                ...configuredStatisticsConfigurations.map((statisticsConfiguration) => {
                    if (statisticsConfiguration.ID === selectedStatisticsConfiguration.ID) {
                        return {
                            ...selectedStatisticsConfiguration,
                            ordering: STATISTICS_ORDERING_NONE
                        }
                    }
                    // Update all fields (if the same ordering priority, remove it, if higher and change is to remove sorting, lower it
                    if (statisticsConfiguration.ordering !== STATISTICS_ORDERING_NONE) {
                        // If existing item has sorting active, check if is not higher than the changed item ordering priority (if so, lower the ordering priority)
                        const orderingPriority = statisticsConfiguration.ordering & STATISTICS_ORDERING_PRIORITY_MASK
                        let newOrderingPriority = orderingPriority
                        const selectedStatisticConfigurationOrderingPriority =
                            selectedStatisticsConfiguration.ordering & STATISTICS_ORDERING_PRIORITY_MASK
                        if (orderingPriority > selectedStatisticConfigurationOrderingPriority) {
                            newOrderingPriority -= 1
                        }

                        if (statisticsConfiguration.ordering !== newOrderingPriority) {
                            return {
                                ...statisticsConfiguration,
                                ordering: newOrderingPriority
                            }
                        }
                        return statisticsConfiguration
                    }
                    return statisticsConfiguration
                })
            ])
        }
        handleSortClose()
    }

    /**
     * Updates sorting settings for currently selected statistics field and updates others accordingly if needed.
     *
     * @param newOrderingPriority New sorting priority (currently 1,2,3) - None value is not supported.
     * @param isDescending Flag whether sorting is ascending or descending.
     */
    const updateSortingForCurrentStatistics = (newOrderingPriority: number, isDescending: boolean): void => {
        const selectedFieldOriginalOrderingPriority =
            selectedStatisticsConfiguration.ordering & STATISTICS_ORDERING_PRIORITY_MASK
        // Set sorting for currently selected statistics
        setConfiguredStatisticsConfigurations([
            ...configuredStatisticsConfigurations.map((statisticsConfiguration) => {
                if (statisticsConfiguration.ID === selectedStatisticsConfiguration.ID) {
                    const newOrderingValue = isDescending
                        ? newOrderingPriority | STATISTICS_ORDERING_ISDESCENDING
                        : newOrderingPriority

                    return {
                        ...selectedStatisticsConfiguration,
                        ordering: newOrderingValue
                    }
                }
                // If existing item has sorting active
                if (statisticsConfiguration.ordering !== STATISTICS_ORDERING_NONE) {
                    // If it is the same ordering priority as set for new sorted field, swap the values.
                    if (
                        (statisticsConfiguration.ordering & STATISTICS_ORDERING_PRIORITY_MASK) ===
                        newOrderingPriority
                    ) {
                        const wasDescending =
                            (statisticsConfiguration.ordering & STATISTICS_ORDERING_ISDESCENDING) ===
                            STATISTICS_ORDERING_ISDESCENDING
                        return {
                            ...statisticsConfiguration,
                            ordering: wasDescending
                                ? selectedFieldOriginalOrderingPriority | STATISTICS_ORDERING_ISDESCENDING
                                : selectedFieldOriginalOrderingPriority
                        }
                    }
                    return statisticsConfiguration
                }
                return statisticsConfiguration
            })
        ])
        // Update all fields (if the same ordering priority, remove it, if higher and change is to remove sorting, lower it
        handleSortClose()
    }

    return (
        <div>
            <Ul className='popup-list'>
                <Li key={language?.get(SortListLanguage.SortAsc1Label)} className='popup-list-item'>
                    <Checkbox
                        isChecked={
                            selectedStatisticsFieldOrderingPriority === 1 &&
                            !selectedStatisticsFieldOrderingValueIsDescending
                        }
                        onChange={(): void =>
                            updateSortingForCurrentStatistics(STATISTICS_ORDERING_PRIORITY_1ST, false)
                        }
                        labelRight={language?.get(SortListLanguage.SortAsc1Label)}
                        title={language?.get(SortListLanguage.SortAsc1Tooltip)}
                        fullWidth
                        id={language?.get(SortListLanguage.SortAsc1Label)}
                    />
                </Li>
                <Li key={language?.get(SortListLanguage.SortDsc1Label)} className='popup-list-item'>
                    <Checkbox
                        isChecked={
                            selectedStatisticsFieldOrderingPriority === 1 &&
                            selectedStatisticsFieldOrderingValueIsDescending
                        }
                        onChange={(): void => updateSortingForCurrentStatistics(STATISTICS_ORDERING_PRIORITY_1ST, true)}
                        labelRight={language?.get(SortListLanguage.SortDsc1Label)}
                        fullWidth
                        id={language?.get(SortListLanguage.SortDsc1Label)}
                        title={language?.get(SortListLanguage.SortDsc1Tooltip)}
                    />
                </Li>

                {configuredStatisticsWithSortingCount > 0 &&
                    !(
                        configuredStatisticsWithSortingCount === 1 &&
                        selectedStatisticsFieldOrderingPriority === STATISTICS_ORDERING_PRIORITY_1ST
                    ) && (
                        <>
                            <Li key={language?.get(SortListLanguage.SortAsc2Label)} className='popup-list-item'>
                                <Checkbox
                                    isChecked={
                                        selectedStatisticsFieldOrderingPriority === 2 &&
                                        !selectedStatisticsFieldOrderingValueIsDescending
                                    }
                                    onChange={(): void =>
                                        updateSortingForCurrentStatistics(STATISTICS_ORDERING_PRIORITY_2ND, false)
                                    }
                                    labelRight={language?.get(SortListLanguage.SortAsc2Label)}
                                    fullWidth
                                    id={language?.get(SortListLanguage.SortAsc2Label)}
                                    title={language?.get(SortListLanguage.SortAsc2Tooltip)}
                                />
                            </Li>
                            <Li key={language?.get(SortListLanguage.SortDsc2Label)} className='popup-list-item'>
                                <Checkbox
                                    isChecked={
                                        selectedStatisticsFieldOrderingPriority === 2 &&
                                        selectedStatisticsFieldOrderingValueIsDescending
                                    }
                                    onChange={(): void =>
                                        updateSortingForCurrentStatistics(STATISTICS_ORDERING_PRIORITY_2ND, true)
                                    }
                                    labelRight={language?.get(SortListLanguage.SortDsc2Label)}
                                    fullWidth
                                    id={language?.get(SortListLanguage.SortDsc2Label)}
                                    title={language?.get(SortListLanguage.SortDsc2Tooltip)}
                                />
                            </Li>
                        </>
                    )}

                {/* Only display 3rd option if there are 2 sorts active and the currently selected field is not 1st or 2nd sort. */}
                {shouldRender3rdSortingOption && (
                    <>
                        <Li key={language?.get(SortListLanguage.SortAsc3Label)} className='popup-list-item'>
                            <Checkbox
                                isChecked={
                                    selectedStatisticsFieldOrderingPriority === 3 &&
                                    !selectedStatisticsFieldOrderingValueIsDescending
                                }
                                onChange={(): void =>
                                    updateSortingForCurrentStatistics(STATISTICS_ORDERING_PRIORITY_3RD, false)
                                }
                                labelRight={language?.get(SortListLanguage.SortAsc3Label)}
                                fullWidth
                                id={language?.get(SortListLanguage.SortAsc3Label)}
                                title={language?.get(SortListLanguage.SortAsc3Tooltip)}
                            />
                        </Li>
                        <Li key={language?.get(SortListLanguage.SortDsc3Label)} className='popup-list-item'>
                            <Checkbox
                                isChecked={
                                    selectedStatisticsFieldOrderingPriority === 3 &&
                                    selectedStatisticsFieldOrderingValueIsDescending
                                }
                                onChange={(): void =>
                                    updateSortingForCurrentStatistics(STATISTICS_ORDERING_PRIORITY_3RD, true)
                                }
                                labelRight={language?.get(SortListLanguage.SortDsc3Label)}
                                fullWidth
                                id={language?.get(SortListLanguage.SortDsc3Label)}
                                title={language?.get(SortListLanguage.SortDsc3Tooltip)}
                            />
                        </Li>
                    </>
                )}

                <Li key={language?.get(SortListLanguage.SortNoneLabel)} className='popup-list-item'>
                    <Checkbox
                        isChecked={notSorted}
                        onChange={setCurrentlySelectedStatisticsConfigurationSortingToNone}
                        labelRight={language?.get(SortListLanguage.SortNoneLabel)}
                        fullWidth
                        id={language?.get(SortListLanguage.SortNoneLabel)}
                        title={language?.get(SortListLanguage.SortNoneTooltip)}
                    />
                </Li>
            </Ul>
        </div>
    )
}

export default SortListContent
